// rpaService.ts - Corrección completa

export interface RPAResult {
  detalle: string;
  error?: string;
  inhabilitado: boolean;
  isAsync?: boolean;
  jobId?: string;
  sistemas: string[];
  eligible_cedulas?: string[];
  non_eligible_cedulas?: string[];
}

export interface IndividualRPAResponse {
  completed: number;
  failed: number;
  message: string;
  processing_type: 'immediate';
  results: Array<{
    cedula: string;
    method?: string;
    results: Record<string, unknown>;
    status: string;
  }>;
  status: string;
  systems_consulted: string[];
  total_cedulas: number;
}

export interface BatchRPAResponse {
  estimated_time_minutes: number;
  job_id: string;
  message: string;
  optimization_enabled: boolean;
  processing_type: 'batch_optimized';
  results_url: string;
  status: 'QUEUED';
  status_url: string;
  systems: string[];
  total_cedulas: number;
}

const MAX_INDIVIDUAL_CEDULAS = 5;

export async function consultarRPA(cedulas: string[]): Promise<RPAResult> {
  console.log('[RPA DEBUG][rpaService] consultando RPA para cédulas:', cedulas)

  if (!cedulas || cedulas.length === 0) {
    return {
      inhabilitado: false,
      detalle: 'No hay cédulas para consultar',
      sistemas: [],
      error: 'No hay cédulas válidas'
    }
  }

  try {
    // *** LÓGICA DUAL CORREGIDA ***
    const useIndividualEndpoint = cedulas.length <= MAX_INDIVIDUAL_CEDULAS;

    if (useIndividualEndpoint) {
      console.log(`[RPA DEBUG][rpaService] Usando endpoint INDIVIDUAL para ${cedulas.length} cédulas`)
      return await consultarRPAIndividual(cedulas);
    } else {
      console.log(`[RPA DEBUG][rpaService] Usando endpoint MASIVO para ${cedulas.length} cédulas`)
      return await consultarRPAMasivo(cedulas);
    }

  } catch (error) {
    console.error('[RPA DEBUG][rpaService] error en consulta RPA:', error)
    return {
      inhabilitado: false,
      detalle: `Error de conexión: ${(error as Error).message}`,
      sistemas: [],
      error: (error as Error).message
    }
  }
}

// --- LÓGICA CENTRALIZADA DE ELEGIBILIDAD ---
function esCedulaElegible(result: any): boolean {
  // Si no hay resultados, se asume elegible
  if (!result || typeof result !== 'object' || !result.results) return true;
  // Si algún sistema reporta propiedad, NO es elegible
  for (const [system, sysResult] of Object.entries(result.results as Record<string, any>)) {
    if (tienePropiedad(system, sysResult)) {
      return false;
    }
  }
  return true;
}

function tienePropiedad(system: string, result: any): boolean {
  if (!result || result?.status !== "success") return false;
  const data = Array.isArray(result?.data) ? result.data : [];
  switch (system) {
    case "mivivienda":
      // Si tiene_inmuebles es true o hay data, tiene propiedad
      return result?.tiene_inmuebles === true || data.length > 0;
    case "vur":
    case "catastro":
      return data.length > 0;
    default:
      return false;
  }
}

async function consultarRPAIndividual(cedulas: string[]): Promise<RPAResult> {
  console.log('[RPA DEBUG][rpaService] consultarRPAIndividual - cédulas:', cedulas)

  try {
    const response = await fetch('/rpa', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      },
      body: JSON.stringify({
        cedulas,
        force_async: false,
        systems: ['vur', 'catastro', 'mivivienda']
      }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP Error: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const data: IndividualRPAResponse = await response.json()
    console.log('[RPA DEBUG][rpaService] respuesta individual RPA:', data)

    // --- PROCESAR TODAS LAS CÉDULAS ---
    let elegibles: string[] = []
    let noElegibles: string[] = []
    let inhabilitado = false
    const sistemasEncontrados: string[] = []

    for (const result of data.results) {
      if (result.status === "COMPLETED" || result.status === "PARTIAL") {
        if (esCedulaElegible(result)) {
          elegibles.push(result.cedula)
        } else {
          noElegibles.push(result.cedula)
          for (const [system, sysResult] of Object.entries(result.results as Record<string, any>)) {
            if ((sysResult as any)?.status === "success" && tienePropiedad(system, sysResult)) {
              sistemasEncontrados.push(system)
            }
          }
          inhabilitado = true
        }
      }
    }

    console.log('[RPA DEBUG][rpaService] resultado individual - inhabilitado:', inhabilitado, 'sistemas:', sistemasEncontrados, 'elegibles:', elegibles, 'noElegibles:', noElegibles)

    return {
      inhabilitado,
      detalle: JSON.stringify(data),
      sistemas: Array.from(new Set(sistemasEncontrados)),
      isAsync: false,
      eligible_cedulas: elegibles,
      non_eligible_cedulas: noElegibles
    }

  } catch (error) {
    console.error('[RPA DEBUG][rpaService] error en consulta individual:', error)
    throw error
  }
}

async function consultarRPAMasivo(cedulas: string[]): Promise<RPAResult> {
  console.log('[RPA DEBUG][rpaService] consultarRPAMasivo - cédulas:', cedulas)

  try {
    const response = await fetch('/rpa/batch', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      },
      body: JSON.stringify({
        cedulas,
        force_async: true,
        systems: ['vur', 'catastro', 'mivivienda']
      }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP Error: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const data: BatchRPAResponse = await response.json()
    console.log('[RPA DEBUG][rpaService] respuesta batch RPA:', data)

    // *** VERIFICAR QUE TENEMOS job_id ***
    if (!data.job_id) {
      throw new Error('No se recibió job_id del servidor RPA')
    }

    // Para consultas batch, retornar job_id para polling posterior
    return {
      inhabilitado: false, // Se determinará después del polling
      detalle: JSON.stringify(data),
      sistemas: [],
      eligible_cedulas: [],
      non_eligible_cedulas: [],
      jobId: data.job_id,
      isAsync: true
    }

  } catch (error) {
    console.error('[RPA DEBUG][rpaService] error en consulta masiva:', error)
    throw error
  }
}


// *** FUNCIÓN PARA OBTENER RESULTADOS CONSOLIDADOS ***
export async function consultarConsolidado(jobId: string): Promise<RPAResult> {
  console.log('[RPA DEBUG][rpaService] consultando consolidado para job:', jobId)

  if (!jobId) {
    throw new Error('Job ID es requerido')
  }

  try {
    const response = await fetch(`/rpa/job/${jobId}/consolidated`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      },
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP Error: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const data = await response.json()
    console.log('[RPA DEBUG][rpaService] respuesta consolidado:', data)

    if (!data) {
      throw new Error('Respuesta vacía del consolidado')
    }

    // --- PROCESAR CONSOLIDADO PARA OBTENER ELEGIBLES Y NO ELEGIBLES ---
    let elegibles: string[] = []
    let noElegibles: string[] = []
    let inhabilitado = false

    if (Array.isArray(data.results)) {
      for (const result of data.results) {
        if (result.status === 'COMPLETED' || result.status === 'PARTIAL') {
          if (esCedulaElegible(result)) {
            elegibles.push(result.cedula)
          } else {
            noElegibles.push(result.cedula)
            inhabilitado = true
          }
        }
      }
    }

    return {
      inhabilitado,
      detalle: JSON.stringify(data),
      sistemas: [],
      eligible_cedulas: elegibles,
      non_eligible_cedulas: noElegibles,
      isAsync: false,
      jobId
    }
  } catch (error) {
    console.error('[RPA DEBUG][rpaService] error consultando consolidado:', error)
    throw new Error(`Error obteniendo consolidado: ${(error as Error).message}`)
  }
}

// *** FUNCIONES CORREGIDAS CON SEMÁNTICA CLARA ***
export function obtenerCedulasSinPropiedades(input: any): string[] {
  console.log('[RPA DEBUG][rpaService] obtenerCedulasSinPropiedades - input:', input)

  // --- CORRECCIÓN: Parsear detalle si es string JSON ---
  let consolidado = input?.consolidated || input
  if (typeof consolidado?.detalle === 'string') {
    try {
      const parsed = JSON.parse(consolidado.detalle)
      if (parsed && typeof parsed === 'object') {
        consolidado = parsed
      }
    } catch (e) {
      // No es JSON válido, continuar con el objeto original
    }
  }

  try {
    // --- FLUJO MASIVO: usar eligible_cedulas si existe ---
    if (Array.isArray(consolidado?.eligible_cedulas)) {
      const elegibles = consolidado.eligible_cedulas
      if (elegibles.length === 0) return []
      const sinPropiedades = elegibles
        .map((item: any) => {
          if (typeof item === 'string') return item
          if (item && typeof item === 'object' && item.cedula) return item.cedula
          return null
        })
        .filter((cedula: any) => Boolean(cedula))
        .map((cedula: any) => String(cedula).trim())
        .filter((cedula: any) => cedula.length > 0)
      return Array.from(new Set(sinPropiedades))
    }
    // --- FLUJO INDIVIDUAL: usar results y esCedulaElegible ---
    if (Array.isArray(consolidado?.results)) {
      const elegibles = consolidado.results
        .filter((result: any) =>
          (result.status === 'COMPLETED' || result.status === 'PARTIAL') &&
          esCedulaElegible(result)
        )
        .map((result: any) => result.cedula)
        .filter((cedula: any) => Boolean(cedula))
        .map((cedula: any) => String(cedula).trim())
        .filter((cedula: any) => cedula.length > 0)
      return Array.from(new Set(elegibles))
    }
    // Si no hay datos válidos
    return []
  } catch (error) {
    console.error('[RPA DEBUG][rpaService] error obteniendo cédulas sin propiedades:', error)
    return []
  }
}

export function obtenerCedulasConPropiedades(input: any): string[] {
  console.log('[RPA DEBUG][rpaService] obtenerCedulasConPropiedades - input:', input)

  const consolidado = input?.consolidated || input

  try {
    // --- FLUJO MASIVO: usar non_eligible_cedulas si existe ---
    if (Array.isArray(consolidado?.non_eligible_cedulas)) {
      const noElegibles = consolidado.non_eligible_cedulas
      if (noElegibles.length === 0) return []
      const conPropiedades = noElegibles
        .map((item: any) => {
          if (typeof item === 'string') return item
          if (item && typeof item === 'object' && item.cedula) return item.cedula
          return null
        })
        .filter((cedula: any) => Boolean(cedula))
        .map((cedula: any) => String(cedula).trim())
        .filter((cedula: any) => cedula.length > 0)
      return Array.from(new Set(conPropiedades))
    }
    // --- FLUJO INDIVIDUAL: usar results y esCedulaElegible ---
    if (Array.isArray(consolidado?.results)) {
      const noElegibles = consolidado.results
        .filter((result: any) =>
          (result.status === 'COMPLETED' || result.status === 'PARTIAL') &&
          !esCedulaElegible(result)
        )
        .map((result: any) => result.cedula)
        .filter((cedula: any) => Boolean(cedula))
        .map((cedula: any) => String(cedula).trim())
        .filter((cedula: any) => cedula.length > 0)
      return Array.from(new Set(noElegibles))
    }
    // Si no hay datos válidos
    return []
  } catch (error) {
    console.error('[RPA DEBUG][rpaService] error obteniendo cédulas con propiedades:', error)
    return []
  }
}

// *** ALIAS SEMÁNTICAMENTE CLAROS PARA CONVOCATORIAS ***
export function filtrarCedulasElegiblesConvocatoria(input: any): string[] {
  // *** PARA CONVOCATORIAS DE VIVIENDA: ELEGIBLES SON LAS QUE NO TIENEN PROPIEDADES ***
  return obtenerCedulasSinPropiedades(input)
}

export function filtrarCedulasNoElegiblesConvocatoria(input: any): string[] {
  // *** PARA CONVOCATORIAS DE VIVIENDA: NO ELEGIBLES SON LAS QUE SÍ TIENEN PROPIEDADES ***
  return obtenerCedulasConPropiedades(input)
}

// *** MANTENER COMPATIBILIDAD CON FUNCIONES ANTERIORES ***
export function filtrarCedulasHabilitadas(input: any): string[] {
  console.warn('[RPA DEBUG] DEPRECADO: Usar filtrarCedulasElegiblesConvocatoria')
  return filtrarCedulasElegiblesConvocatoria(input)
}

export function filtrarCedulasInhabilitadas(input: any): string[] {
  console.warn('[RPA DEBUG] DEPRECADO: Usar filtrarCedulasNoElegiblesConvocatoria')
  return filtrarCedulasNoElegiblesConvocatoria(input)
}

export async function validarEstadoJob(jobId: string): Promise<{status: string, completed: boolean, failed: boolean}> {
  try {
    const response = await fetch(`/rpa/status/${jobId}`)
    if (!response.ok) {
      throw new Error(`HTTP Error: ${response.status}`)
    }

    const data = await response.json()
    return {
      status: data.status || 'UNKNOWN',
      completed: data.status === 'COMPLETED',
      failed: data.status === 'FAILED' || data.status === 'ERROR'
    }
  } catch (error) {
    console.error('[RPA DEBUG][rpaService] error validando estado de job:', error)
    return {
      status: 'ERROR',
      completed: false,
      failed: true
    }
  }
}